import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import type { ExploreVrBannerData } from '@/core/services/contentfulClient';
import {
  viewLayoutDirection,
  webViewDirProps,
} from '@/core/utils/layoutDirection';
import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import {
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type TextStyle,
} from 'react-native';

export interface ExploreVrBannerProps {
  readonly banner: ExploreVrBannerData;
  /** Replaces `{{projectName}}` in the Contentful title template. */
  readonly projectName?: string;
  readonly compact?: boolean;
  /**
   * Pins label/title to the logical start edge (right in Arabic) instead of
   * relying on textAlign. Opt-in so web's AdhaListingProjectPanel is unchanged.
   */
  readonly alignTextToStart?: boolean;
  /**
   * Compact only: vertically centres the copy + CTA row inside the card.
   * Opt-in so web's AdhaListingProjectPanel is unchanged.
   */
  readonly centerContent?: boolean;
  /** Fired when the user taps the CTA, before opening the tour URL. */
  readonly onStartTourPress?: () => void;
}

/** Figma banner frame height/radius; width matches left project column. */
const BANNER_HEIGHT = 136;
/** Phone: shorter type + auto height so label/title/CTA stay fully visible. */
const BANNER_HEIGHT_COMPACT_MIN = 112;
const BANNER_RADIUS = 16;

/**
 * Named Matter faces already include weight. Extra fontWeight synthesizes bold
 * on web and looks heavy/blurry — keep weight normal with the face file.
 */
const webTextClarity =
  Platform.OS === 'web'
    ? ({
        fontWeight: '400' as const,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      } as const)
    : ({} as const);

function resolveBannerTitle(
  template: string,
  projectName: string | undefined,
): string {
  const name = projectName?.trim() ?? '';
  if (!template.includes('{{projectName}}')) {
    return template;
  }
  return template.replace(/\{\{\s*projectName\s*\}\}/g, name).replace(/\s+/g, ' ').trim();
}

function openExploreVrTourUrl(url: string): void {
  const href = url.trim();
  if (!href) {
    return;
  }
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(href, '_blank', 'noopener,noreferrer');
    return;
  }
  Linking.openURL(href).catch(() => {
    // Non-critical external navigation.
  });
}

function ExploreVrBannerBackground({
  backgroundImageUrl,
  fallbackColor,
}: Readonly<{
  backgroundImageUrl?: string | undefined;
  fallbackColor: string;
}>) {
  if (backgroundImageUrl) {
    return (
      <Image
        source={{ uri: backgroundImageUrl }}
        style={styles.background}
        contentFit="cover"
        accessibilityIgnoresInvertColors
      />
    );
  }
  return <View style={[styles.background, { backgroundColor: fallbackColor }]} />;
}

/** Web textAlign is physical. Native `left` is mirrored by the banner `direction`. */
function bannerCopyTextAlign(isRTL: boolean): TextStyle['textAlign'] {
  if (Platform.OS !== 'web') {
    return 'left';
  }
  if (isRTL) {
    return 'right';
  }
  return 'left';
}

function BannerCopyLine({
  text,
  baseStyle,
  compactStyle,
  rtlStyle,
  compact,
  isRTL,
  textAlign,
  writingDirection,
  numberOfLines,
}: Readonly<{
  text?: string | undefined;
  baseStyle: TextStyle;
  compactStyle: TextStyle;
  rtlStyle: TextStyle;
  compact: boolean;
  isRTL: boolean;
  textAlign: TextStyle['textAlign'];
  writingDirection: TextStyle['writingDirection'];
  numberOfLines: number;
}>) {
  if (!text) {
    return null;
  }
  return (
    <Text
      style={[
        baseStyle,
        compact && compactStyle,
        isRTL && rtlStyle,
        { textAlign, writingDirection },
      ]}
      numberOfLines={numberOfLines}
    >
      {text}
    </Text>
  );
}

function ExploreVrBannerCopy({
  label,
  title,
  compact,
  alignTextToStart,
  isRTL,
}: Readonly<{
  label?: string | undefined;
  title: string;
  compact: boolean;
  alignTextToStart: boolean;
  isRTL: boolean;
}>) {
  const textAlign = bannerCopyTextAlign(isRTL);
  const writingDirection: TextStyle['writingDirection'] = isRTL ? 'rtl' : 'ltr';
  const labelRtlStyle = compact ? styles.labelCompactRtl : styles.labelRtl;
  const titleRtlStyle = compact ? styles.titleCompactRtl : styles.titleRtl;

  return (
    <View style={[styles.textCol, alignTextToStart && styles.textColStart]}>
      <BannerCopyLine
        text={label}
        baseStyle={styles.label}
        compactStyle={styles.labelCompact}
        rtlStyle={labelRtlStyle}
        compact={compact}
        isRTL={isRTL}
        textAlign={textAlign}
        writingDirection={writingDirection}
        numberOfLines={compact ? 2 : 1}
      />
      <BannerCopyLine
        text={title}
        baseStyle={styles.title}
        compactStyle={styles.titleCompact}
        rtlStyle={titleRtlStyle}
        compact={compact}
        isRTL={isRTL}
        textAlign={textAlign}
        writingDirection={writingDirection}
        numberOfLines={compact ? 3 : 2}
      />
    </View>
  );
}

function ExploreVrBannerCtaIcon({
  buttonIconUrl,
  compact,
  iconColor,
}: Readonly<{
  buttonIconUrl?: string | undefined;
  compact: boolean;
  iconColor: string;
}>) {
  if (buttonIconUrl) {
    return (
      <Image
        source={{ uri: buttonIconUrl }}
        style={[styles.ctaIcon, compact && styles.ctaIconCompact]}
        contentFit="contain"
      />
    );
  }
  return (
    <View
      style={[
        styles.ctaIconFallback,
        compact && styles.ctaIconCompact,
        { borderColor: iconColor },
      ]}
    >
      <View style={[styles.ctaPlayTriangle, { borderLeftColor: iconColor }]} />
    </View>
  );
}

function ExploreVrBannerCta({
  buttonName,
  accessibilityLabel,
  buttonIconUrl,
  compact,
  iconColor,
  isRTL,
  centered,
  onPress,
}: Readonly<{
  buttonName?: string | undefined;
  accessibilityLabel: string;
  buttonIconUrl?: string | undefined;
  compact: boolean;
  iconColor: string;
  isRTL: boolean;
  centered: boolean;
  onPress: () => void;
}>) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [
        styles.cta,
        compact && styles.ctaCompact,
        centered && styles.ctaCentered,
        pressed && styles.ctaPressed,
      ]}
    >
      <ExploreVrBannerCtaIcon
        buttonIconUrl={buttonIconUrl}
        compact={compact}
        iconColor={iconColor}
      />
      {buttonName ? (
        <Text
          style={[
            styles.ctaLabel,
            compact && styles.ctaLabelCompact,
            { writingDirection: isRTL ? 'rtl' : 'ltr' },
          ]}
          numberOfLines={1}
        >
          {buttonName}
        </Text>
      ) : null}
    </Pressable>
  );
}

/**
 * Contentful-driven promo banner — VR tour and master plan share this layout.
 * Only render when `banner` is provided by CMS.
 */
export function ExploreVrBanner({
  banner,
  projectName,
  compact = false,
  alignTextToStart = false,
  centerContent = false,
  onStartTourPress,
}: Readonly<ExploreVrBannerProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const title = useMemo(
    () => resolveBannerTitle(banner.title, projectName),
    [banner.title, projectName],
  );
  const direction = isRTL ? 'rtl' : 'ltr';
  const hasCopy = Boolean(banner.label?.trim() || title);
  const centered = compact && centerContent;

  if (!banner.startTourLink.trim() || !hasCopy) {
    return null;
  }

  return (
    <View
      style={[
        styles.root,
        compact && styles.rootCompact,
        Platform.OS !== 'web' ? viewLayoutDirection(direction) : null,
      ]}
      {...webViewDirProps(direction)}
      accessibilityRole="summary"
    >
      <ExploreVrBannerBackground
        backgroundImageUrl={banner.backgroundImageUrl}
        fallbackColor={colors.fontPrimary}
      />
      <View style={styles.overlay} />

      <View
        style={[
          styles.content,
          compact && styles.contentCompact,
          centered && styles.contentCentered,
        ]}
      >
        <ExploreVrBannerCopy
          label={banner.label}
          title={title}
          compact={compact}
          alignTextToStart={alignTextToStart}
          isRTL={isRTL}
        />
        <ExploreVrBannerCta
          buttonName={banner.buttonName}
          accessibilityLabel={banner.buttonName || title || banner.label}
          buttonIconUrl={banner.buttonIconUrl}
          compact={compact}
          iconColor={colors.fontPrimary}
          isRTL={isRTL}
          centered={centered}
          onPress={() => {
            onStartTourPress?.();
            openExploreVrTourUrl(banner.startTourLink);
          }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    minHeight: BANNER_HEIGHT,
    borderRadius: BANNER_RADIUS,
    overflow: 'hidden',
    position: 'relative',
    justifyContent: 'center',
    opacity: 1,
    alignSelf: 'stretch',
  },
  rootCompact: {
    width: '100%',
    height: 'auto',
    minHeight: BANNER_HEIGHT_COMPACT_MIN,
  },
  background: {
    ...StyleSheet.absoluteFillObject,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    minHeight: BANNER_HEIGHT,
    paddingVertical: 24,
    paddingHorizontal: 24,
    zIndex: 1,
  },
  contentCompact: {
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    paddingVertical: 14,
    paddingHorizontal: 14,
    gap: 10,
    height: 'auto',
    minHeight: BANNER_HEIGHT_COMPACT_MIN,
  },
  // The wrapped row sits in the middle of the card's min-height instead of
  // at the top; the CTA lines up with the middle of the copy.
  contentCentered: {
    alignItems: 'center',
    alignContent: 'center',
  },
  textCol: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  // Logical start: label/title boxes hug the left edge in English and the
  // right edge in Arabic, independent of textAlign.
  textColStart: {
    alignItems: 'flex-start',
  },
  /** Font/Size/Base SemiBold 16 · LH 100% */
  label: {
    fontFamily: 'Matter-SemiBold',
    fontSize: 16,
    lineHeight: 16,
    letterSpacing: 0,
    color: 'rgba(255, 255, 255, 0.85)',
    ...webTextClarity,
  },
  /** Phone: readable without clipping under fixed desktop sizes. */
  labelCompact: {
    fontSize: 12,
    lineHeight: 16,
  },
  /** Arabic diacritics clip at a 1:1 line height. */
  labelRtl: {
    lineHeight: 22,
  },
  labelCompactRtl: {
    lineHeight: 18,
  },
  /** Font/Size/2XL Medium 28 · LH 100% */
  title: {
    fontFamily: 'Matter-Medium',
    fontSize: 28,
    lineHeight: 28,
    letterSpacing: 0,
    color: '#FFFFFF',
    ...webTextClarity,
  },
  titleCompact: {
    fontSize: 15,
    lineHeight: 20,
  },
  titleRtl: {
    lineHeight: 36,
  },
  titleCompactRtl: {
    lineHeight: 22,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    alignSelf: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 999,
    paddingVertical: 12,
    paddingHorizontal: 20,
    flexShrink: 1,
    maxWidth: '52%',
    ...(Platform.OS === 'web'
      ? ({ cursor: 'pointer' } as object)
      : {}),
  },
  ctaCompact: {
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: 6,
    maxWidth: '100%',
    flexShrink: 0,
  },
  ctaCentered: {
    alignSelf: 'center',
  },
  ctaPressed: {
    opacity: 0.9,
  },
  ctaIcon: {
    width: 16,
    height: 16,
  },
  ctaIconCompact: {
    width: 14,
    height: 14,
  },
  ctaIconFallback: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaPlayTriangle: {
    width: 0,
    height: 0,
    marginLeft: 1,
    borderStyle: 'solid',
    borderTopWidth: 3,
    borderBottomWidth: 3,
    borderLeftWidth: 5,
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
  },
  /** Figma: Inter SemiBold 12.5 / 18.75 — Matter-SemiBold (app font stack). */
  ctaLabel: {
    fontFamily: 'Matter-SemiBold',
    fontSize: 12.5,
    lineHeight: 18.75,
    letterSpacing: 0,
    textAlign: 'center',
    color: '#1A1A1A',
    flexShrink: 1,
    ...webTextClarity,
  },
  ctaLabelCompact: {
    fontSize: 11,
    lineHeight: 14,
    flexShrink: 1,
  },
});

export default ExploreVrBanner;
